import * as ACTIONS from 'constant/action/insight'
import * as ACTION from 'constant/action'
import * as LOADER_ACTIONS from 'constant/action/loader'
import * as PAGINATION from 'constant/pagination'
import * as TYPE from 'constant/user-types'
import * as TABLE from 'constant/table'
import * as NUMBER from 'constant/number'
import buildFileMeta from 'helper/build-file-meta'
import buildImageFile from 'helper/build-image-file'
import { buildHTML } from 'helper/build-html'
import { downloadLicense } from 'action/article'
import isDraft from 'helper/is-draft'
import isPublished from 'helper/is-published'
import { createAction } from 'redux-actions'
import _ from 'lodash'
import buildDescriptionIframes from 'helper/build-description-iframes'
import html2pdf from 'html-to-pdf-js'

const updateInsights = createAction(ACTIONS.UPDATE_INSIGHTS)
const updateInsightSubmitting = createAction(LOADER_ACTIONS.UPDATE_INSIGHT_SUBMITTING)
const updateLoading = createAction(LOADER_ACTIONS.UPDATE_LOADING)
const updatePagination = createAction(ACTIONS.UPDATE_INSIGHT_PAGINATION)
const updateInsightSearchTerm = createAction(ACTIONS.UPDATE_INSIGHT_SEARCH_TERM)

export const getInsights = (sort = PAGINATION.SORT.INSIGHTS, direction = PAGINATION.DESCENDING, start = NUMBER.ZERO) => async (dispatch, getState, { insight }) => {
  dispatch(updateLoading(true))
  dispatch(updatePagination({
    direction,
    sort,
    start
  }))

  const {
    insight: insightState
  } = getState()

  const insights = await insight.getInsights(sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(insightState, 'insight.start'))
  dispatch(updateInsights(insights))
  dispatch(updateLoading(false))
}

export const searchInsights = (searchTerm, sort = PAGINATION.SORT.ARTICLES, direction = PAGINATION.DESCENDING, start = NUMBER.ZERO) => async (dispatch, getState, { insight }) => {
  dispatch(updateInsightSearchTerm(searchTerm))

  if (!searchTerm) {
    return dispatch(getInsights())
  }

  dispatch(updateLoading(true))
  dispatch(updatePagination({
    direction,
    sort,
    start
  }))
  const {
    insight: insightState
  } = getState()
  const response = await insight.searchInsights(searchTerm, sort, direction, TABLE.PAGE_SIZE, _.isNumber(start) ? start : _.get(insightState, 'insight.start'))
  dispatch(updateInsights(response))
  dispatch(updateLoading(false))
}

export const addNewInsight = (insightObject) => async (dispatch, getState, { insight }) => {
  try {
    dispatch(updateInsightSubmitting(true))
    insightObject.description_meta = buildDescriptionIframes(insightObject.description_meta)
    insightObject.image_meta = buildImageFile(insightObject.image_meta)

    const {
      file: imageFile,
      meta: imageMeta
    } = buildFileMeta(insightObject.image_meta, insightObject.title)

    const { file: descriptionFile, meta: descriptionMeta } = buildFileMeta(insightObject.description_meta, insightObject.title)

    const insightIsDraft = isDraft(insightObject)

    const newInsight = createInsightObject(insightObject, descriptionMeta, imageMeta, insightIsDraft)

    const response = await insight.addNewInsight(newInsight)
    if (imageFile) {
      await insight.uploadFile(response.image_url, imageFile)
    }

    if (descriptionFile) {
      await insight.uploadFile(response.description_url, descriptionFile)
    }

    const { insight: { pagination: { sort, direction, start } } } = getState()

    dispatch(updateInsightSubmitting(false))
    dispatch(getInsights(sort, direction, start))
    return newInsight
  } catch (error) {
    console.error(error)
    return { error: true, message: error.message }
  }
}

const createInsightObject = (insightObject, descriptionMeta, imageMeta, insightIsDraft) => ({
  ...insightObject,
  article_type_uuid: TYPE.INSIGHT_TYPE,
  description_meta: descriptionMeta,
  image_meta: (imageMeta === ACTION.REMOVED ? undefined : imageMeta),
  is_draft: insightIsDraft,
  published: isPublished(insightIsDraft, insightObject.published),
  send_emails: insightObject.send_emails === 'true'
})

export const editInsight = (insightObject) => async (dispatch, getState, { insight }) => {
  try {
    dispatch(updateInsightSubmitting(true))

    insightObject.image_meta = buildImageFile(insightObject.image_meta)
    insightObject.description_meta = buildDescriptionIframes(insightObject.description_meta)
    const {
      file: imageFile,
      meta: imageMeta
    } = buildFileMeta(insightObject.image_meta, insightObject.title)

    const { file: descriptionFile, meta: descriptionMeta } = buildFileMeta(insightObject.description_meta, insightObject.title)

    const insightIsDraft = isDraft(insightObject)
    const editedInsight = await buildInsightObject(insightObject, descriptionMeta, imageMeta, insightIsDraft)

    const response = await insight.editInsight(editedInsight)

    if (imageFile) {
      await insight.uploadFile(response.image_url, imageFile)
    }

    if (descriptionFile) {
      await insight.uploadFile(response.description_url, descriptionFile)
    }

    const { insight: { pagination: { sort, direction, start } } } = getState()

    dispatch(updateInsightSubmitting(false))
    dispatch(getInsights(sort, direction, start))
    return editedInsight
  } catch (error) {
    dispatch(updateInsightSubmitting(false))
    return { error: true, message: error.message }
  }
}

const buildInsightObject = (insightObject, descriptionMeta, imageMeta, insightIsDraft) => ({
  ...insightObject,
  article_type_uuid: TYPE.INSIGHT_TYPE,
  description_meta: descriptionMeta,
  image_meta: imageMeta,
  is_draft: insightIsDraft,
  published: isPublished(insightIsDraft, insightObject.published),
  send_emails: insightObject.send_emails === 'true'
})

export const deleteInsight = (uuid) => async (dispatch, getState, { article }) => {
  await article.deleteArticle(uuid)
  const {
    insight: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()
  dispatch(getInsights(sort, direction, start))
}

export const publishInsight = (uuid) => async (dispatch, getState, { article }) => {
  await article.publishArticle(uuid)

  const {
    insight: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()

  dispatch(getInsights(sort, direction, start))
}

export const pinInsight = (uuid) => async (dispatch, getState, { article }) => {
  await article.pinArticle(uuid)

  const {
    insight: {
      pagination: {
        sort,
        direction,
        start
      }
    }
  } = getState()

  dispatch(getInsights(sort, direction, start))
}

export const downloadPDF = (html, article) => async (dispatch, getState, { insight }) => {
  try {
    dispatch(downloadLicense(article.site_uuid))
    const opt = {
      filename: article.title,
      html2canvas: { scale: 2 },
      pagebreak: { avoid: ['img', 'table', 'h2', 'h1', 'h4', 'p'] }
    }

    const htmlPDF = await buildHTML(article, html)
    await html2pdf().set(opt).from(htmlPDF).save()

    return true
  } catch (error) {
    console.error(error)
  }
}

export const downloadInsights = () => async (dispatch, getState, { insight }) => {
  try {
    const {
      table: {
        columns: {
          INSIGHT: columns
        }
      }
    } = getState()
    const { objUrl } = await insight.downloadInsights(columns)
    window.open(objUrl)
  } catch (error) {
    console.error(error)
  }
}
